import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom';
import { Banner, Button, Card, Empty, Spinner } from '../components/ui';
import { useSession } from '../lib/session';
import { useAsync } from '../lib/useAsync';
import { ParcelLookup } from './ParcelDoor';
import { isAdmitted, isParcelId, isSeatKind, membershipTarget, parcelDoor, seatLabels } from './model';
import { parcelApi } from './service';

export default function ParcelHub() {
  const { contractor } = useSession();
  return contractor ? <Hub key={contractor.id} /> : null;
}
function Hub() {
  const { contractor } = useSession();
  const list = useAsync(parcelApi.list, []);
  return <div className="space-y-6">
    <div><h1 className="text-xl font-bold">My parcel files</h1>
      <p className="mt-1 text-sm text-muted">Open the file or watch you belong to. Invitations appear here while access is pending.</p></div>
    {list.loading ? <Spinner label="Loading your parcel files…" /> : list.error ?
      <Banner title="Could not load your parcel files">{list.error} <Button variant="secondary" onClick={() => void list.reload()}>Reload files</Button></Banner> :
      !list.data?.memberships.length ? <Card><Empty>No parcel files yet. Open a file from a tracked property or an existing job.</Empty></Card> :
      <div className="grid gap-4 lg:grid-cols-2">{list.data.memberships.map((row) => {
        const target = membershipTarget(row);
        const compatible = (contractor?.accountRole === 'homeowner') === (row.seat === 'homeowner');
        const canJoin = compatible && row.state === 'admission_required' && isParcelId(row.graphParcelId) &&
          isSeatKind(row.seat) && !['homeowner', 'flipper', 'title', 'attorney'].includes(row.seat) && contractor?.permissionLevel !== 'read_only';
        return <Card key={row.membershipId} title={row.address}>
          <div className="space-y-3 text-sm">
            <p className="break-words text-muted">{row.county} · {row.folio}</p>
            <p>{seatLabels[row.seat]}{row.readOnly && row.state === 'ready' ? ' · Read only' : ''}</p>
            {target && compatible ? <Link className="font-semibold text-tint" to={target}>Open {seatLabels[row.seat]}</Link> :
              <p className="text-muted">{row.state === 'support_view_as_only' ? 'Support access uses your separate CS view-as URL.' :
                row.state === 'seller_consent_required' ? 'Waiting for named seller consent.' :
                row.state === 'admission_required' ? 'Invitation awaiting admission.' : 'This file is unavailable for your account role.'}</p>}
            {canJoin && <Link className="font-semibold text-tint" to={`${parcelDoor(row.graphParcelId, row.seat)}/join`}>Review invitation</Link>}
          </div>
        </Card>;
      })}</div>}
    <details><summary className="cursor-pointer text-sm font-semibold text-muted">Find an existing file by county and folio</summary><div className="mt-4"><ParcelLookup /></div></details>
  </div>;
}

export function ParcelResolver() {
  const { id } = useParams();
  const [search] = useSearchParams();
  const { contractor } = useSession();
  if (!isParcelId(id)) return <Banner title="Parcel door not found">Use a parcel file link.</Banner>;
  const token = search.get('t');
  // Run 6: a seat link may omit the seat segment; the token names the door.
  if (token) return <ResolveToken key={`t/${id}`} id={id} token={token} />;
  if (!contractor) return <Banner title="This URL is the seat">Open the full seat link you were sent — it carries its own credential. There is no login that substitutes for it.</Banner>;
  // Contractor field crews keep the phone as their surface; a brokerage field
  // seat is the listing agent (Run 10), whose parcel door may resolve here.
  if (contractor.permissionLevel === 'field' && contractor.companyKind !== 'brokerage') {
    return <Navigate to="/mobile-required" replace />;
  }
  return <Resolve key={`${contractor.id}/${id}`} id={id} />;
}
function ResolveToken({ id, token }: { id: string; token: string }) {
  const file = useAsync(() => parcelApi.file(id, token), [id, token]);
  if (file.loading) return <Spinner label="Opening your parcel door…" />;
  const cap = file.data?.capability;
  if (file.error || !cap || cap.parcelId !== id || !isSeatKind(cap.seat)) {
    return <Banner title="This is not your parcel door">Open the full seat link you were sent. Title and counsel require named seller consent.</Banner>;
  }
  return <Navigate to={`${parcelDoor(id, cap.seat)}?t=${encodeURIComponent(token)}`} replace />;
}
function Resolve({ id }: { id: string }) {
  const { contractor } = useSession();
  const file = useAsync(() => parcelApi.file(id), [id]);
  if (file.loading) return <Spinner label="Opening your parcel door…" />;
  if (file.error) return <Banner title="Parcel file unavailable">{file.error} <Button variant="secondary" onClick={() => void file.reload()}>Reload file</Button></Banner>;
  const snapshot = file.data;
  const own = snapshot?.seats.filter((s) => s.contractorId === contractor?.id && isAdmitted(s));
  if (!snapshot || snapshot.parcel.id !== id || snapshot.packet.parcelId !== id || snapshot.viewAs !== null ||
    own?.length !== 1 || !isSeatKind(own[0].seat)) return <Banner title="This is not your parcel door">An admitted customer seat is required. Title and counsel require named seller consent.</Banner>;
  return <Navigate to={parcelDoor(id, own[0].seat)} replace />;
}
